import { FC, useCallback, useEffect, useState } from 'react';

import {
  ChevronDownIcon,
  CogIcon,
  DocumentDuplicateIcon,
  FolderAddIcon,
  FolderIcon,
  FolderRemoveIcon,
  PencilIcon,
  PlusIcon,
  TagIcon,
  TrashIcon,
} from '@heroicons/react/outline';

import { useProviderLibraryActivitiesLazyQuery } from '../../../../generated/graphql';
import { getAuditActivityTeamMemberDisplay } from '../../../lib/auditActivityDisplay';

import { getFullDateTimeStamp, getRelativeTimestamp } from '../../../lib/time';

import Avatar from '../../Avatar';
import ModalDialog from '../../ModalDialog';
import Spinner from '../../../svgs/Spinner';
import ThumbnailAudio from '../../../svgs/ThumbnailAudio';
import ThumbnailForm from '../../../svgs/ThumbnailForm';
import ThumbnailImage from '../../../svgs/ThumbnailImage';
import ThumbnailLink from '../../../svgs/ThumbnailLink';
import ThumbnailPDF from '../../../svgs/ThumbnailPDF';
import ThumbnailText from '../../../svgs/ThumbnailText';

const PAGE_SIZE = 20;

const RESOURCE_KIND_LABEL: Record<string, string> = {
  audio: 'Audio',
  image: 'Image',
  link: 'Link',
  file: 'File',
  text: 'Text document',
  client_form: 'Client form',
  practitioner_form: 'Practitioner form',
  folder: 'Folder',
};

const CHANGED_FIELD_LABEL: Record<string, string> = {
  title: 'title',
  name: 'name',
  text: 'content',
  description: 'description',
  questions: 'questions',
  completion_settings: 'completion settings',
};

const humanizeFieldList = (fields: string[]): string => {
  const labels = fields.map(
    (f) => CHANGED_FIELD_LABEL[f] ?? f.replace(/_/g, ' ').toLowerCase(),
  );
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
};

const ACTION_LABEL: Record<string, string> = {
  added: 'Added',
  renamed: 'Renamed',
  edited: 'Edited',
  settings_changed: 'Settings changed',
  duplicated: 'Duplicated',
  deleted: 'Deleted',
  folder_created: 'Folder created',
  added_from_discover: 'Added from Discover',
  folder_renamed: 'Folder renamed',
  folder_deleted: 'Folder deleted',
  added_to_folder: 'Added to folder',
  moved_to_folder: 'Moved to folder',
  removed_from_folder: 'Removed from folder',
  restored: 'Restored',
};

const getActionIcon = (activityType: string) => {
  const iconClass = 'h-3.5 w-3.5 shrink-0';
  switch (activityType) {
    case 'added':
      return <PlusIcon className={iconClass} />;
    case 'deleted':
    case 'folder_deleted':
      return <TrashIcon className={iconClass} />;
    case 'renamed':
    case 'folder_renamed':
    case 'edited':
      return <PencilIcon className={iconClass} />;
    case 'duplicated':
      return <DocumentDuplicateIcon className={iconClass} />;
    case 'settings_changed':
      return <CogIcon className={iconClass} />;
    case 'folder_created':
    case 'added_from_discover':
      return <FolderIcon className={iconClass} />;
    case 'added_to_folder':
    case 'moved_to_folder':
      return <FolderAddIcon className={iconClass} />;
    case 'removed_from_folder':
      return <FolderRemoveIcon className={iconClass} />;
    case 'restored':
      return <PlusIcon className={iconClass} />;
    default:
      return <TagIcon className={iconClass} />;
  }
};

const formatFromTo = (
  oldValue: string | null | undefined,
  newValue: string | null | undefined,
): string => {
  const parts: string[] = [];
  if (oldValue) parts.push(`From "${oldValue}"`);
  if (newValue) parts.push(`to "${newValue}"`);
  return parts.join(' ') || '—';
};

const clampedTextClassName = 'line-clamp-2 min-w-0 break-words';

const getResourceLabel = (kind?: string): string => {
  if (!kind) return 'Resource';
  return RESOURCE_KIND_LABEL[kind] ?? kind;
};

const renderResourceThumbnail = (kind?: string) => {
  const className = 'h-6 w-6 flex-none rounded-md';
  switch (kind) {
    case 'audio':
      return <ThumbnailAudio className={className} />;
    case 'image':
      return <ThumbnailImage className={className} />;
    case 'link':
      return <ThumbnailLink className={className} />;
    case 'file':
      return <ThumbnailPDF className={className} />;
    case 'text':
      return <ThumbnailText className={className} />;
    case 'client_form':
      return <ThumbnailForm className={className} isProviderOnly={false} />;
    case 'practitioner_form':
      return <ThumbnailForm className={className} isProviderOnly />;
    case 'folder':
      return (
        <div
          className={`${className} flex items-center justify-center bg-neutral-50`}
        >
          <FolderIcon className="h-3.5 w-3.5 text-neutral-125" />
        </div>
      );
    default:
      return null;
  }
};

type ExtraMetadata = {
  provider_id?: string;
  resource_kind?: string;
  title?: string;
  name?: string;
  source_title?: string;
  old_title?: string;
  new_title?: string;
  old_name?: string;
  new_name?: string;
  changed_fields?: string[] | null;
  from_collection_id?: string;
  from_collection_name?: string;
  to_collection_id?: string;
  to_collection_name?: string;
  old_completion_type?: string;
  new_completion_type?: string;
  old_activity_use_type?: string;
  new_activity_use_type?: string;
  curator_name?: string;
  resource_count?: number;
  description?: string;
};

const getResourceName = (
  metadata: ExtraMetadata | null | undefined,
): string => {
  if (!metadata) return '';
  return (
    metadata.title ?? metadata.name ?? getResourceLabel(metadata.resource_kind)
  );
};

const getDetailsForActivity = (
  activityType: string,
  metadata: ExtraMetadata | null | undefined,
): string => {
  if (!metadata) return '—';
  switch (activityType) {
    case 'renamed':
      return formatFromTo(metadata.old_title, metadata.new_title);
    case 'folder_renamed':
      return formatFromTo(metadata.old_name, metadata.new_name);
    case 'edited': {
      const fields = metadata.changed_fields;
      if (fields && fields.length > 0) {
        return `Updated ${humanizeFieldList(fields)}`;
      }
      return 'Content updated';
    }
    case 'settings_changed': {
      const parts: string[] = [];
      if (metadata.new_completion_type) {
        parts.push(`Completion: ${metadata.new_completion_type}`);
      }
      if (metadata.new_activity_use_type) {
        parts.push(`Use: ${metadata.new_activity_use_type}`);
      }
      return parts.join(' • ') || '—';
    }
    case 'duplicated':
      return metadata.source_title
        ? `Copied from "${metadata.source_title}"`
        : '—';
    case 'added_to_folder':
      return metadata.to_collection_name
        ? `Into "${metadata.to_collection_name}"`
        : '—';
    case 'moved_to_folder':
      return formatFromTo(
        metadata.from_collection_name,
        metadata.to_collection_name,
      );
    case 'removed_from_folder':
      return metadata.from_collection_name
        ? `From "${metadata.from_collection_name}"`
        : '—';
    case 'added_from_discover': {
      const parts: string[] = [];
      if (metadata.curator_name) {
        parts.push(`Curated by ${metadata.curator_name}`);
      }
      if (metadata.resource_count != null) {
        const count = metadata.resource_count;
        parts.push(`${count} resource${count === 1 ? '' : 's'}`);
      }
      return parts.join(' • ') || '—';
    }
    case 'restored':
      return (
        metadata.description ||
        'Restored to main library list. Not placed back in a folder or collection.'
      );
    default:
      return '—';
  }
};

type Activity = {
  id: string;
  entityType: string;
  entityId: string;
  activityType: string;
  actorType?: string | null;
  performedByProviderUser?: {
    id: string;
    name?: string | null;
    profileImageMedia?: { url: string } | null;
  } | null;
  performedAt: string;
  extraMetadata?: unknown;
};

type LibraryActivityModalProps = {
  isOpen: boolean;
  setClosed: () => void;
};

const LibraryActivityModal: FC<LibraryActivityModalProps> = ({
  isOpen,
  setClosed,
}) => {
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allActivities, setAllActivities] = useState<Activity[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [fetchActivities, { loading }] = useProviderLibraryActivitiesLazyQuery({
    fetchPolicy: 'network-only',
  });

  const loadPage = useCallback(
    async (pageOffset: number, append: boolean) => {
      setLoadError(null);
      const result = await fetchActivities({
        variables: { limit: PAGE_SIZE, offset: pageOffset },
      });
      if (result.error) {
        setLoadError('Unable to load Library activity. Please try again.');
        setHasMore(false);
        return 0;
      }
      const next = (result.data?.providerLibraryActivities ?? []) as Activity[];
      setAllActivities((prev) => (append ? [...prev, ...next] : next));
      setOffset(pageOffset + next.length);
      setHasMore(next.length >= PAGE_SIZE);
      return next.length;
    },
    [fetchActivities],
  );

  useEffect(() => {
    if (isOpen) {
      setOffset(0);
      setAllActivities([]);
      setHasMore(true);
      setLoadError(null);
      loadPage(0, false);
    }
  }, [isOpen, loadPage]);

  const handleLoadMore = useCallback(() => {
    loadPage(offset, true);
  }, [loadPage, offset]);

  return (
    <ModalDialog
      title="Library activity"
      isOpen={isOpen}
      setClosed={setClosed}
      fetching={false}
      width="large"
      overflow="auto"
      hideFooter
      bodyClassName="min-h-[200px]"
      className="sm:min-w-[900px]"
    >
      <div className="flex flex-col">
        {loading && allActivities.length === 0 ? (
          <div className="flex justify-center py-12">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <>
            {allActivities.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-neutral-75">
                <table className="w-full min-w-[900px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-neutral-75 bg-neutral-25">
                      <th className="whitespace-nowrap px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Team member
                      </th>
                      <th className="whitespace-nowrap px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Action
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Resource
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Details
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allActivities.map((activity) => {
                      const teamMember =
                        getAuditActivityTeamMemberDisplay(activity);
                      const actionLabel =
                        ACTION_LABEL[activity.activityType] ??
                        activity.activityType;
                      const metadata = (activity.extraMetadata ??
                        null) as ExtraMetadata | null;
                      const resourceName = getResourceName(metadata) || '—';
                      const details = getDetailsForActivity(
                        activity.activityType,
                        metadata,
                      );

                      return (
                        <tr
                          key={activity.id}
                          className={
                            teamMember.isSystem
                              ? 'border-b border-neutral-50 bg-secondary-20 last:border-b-0'
                              : 'border-b border-neutral-50 last:border-b-0'
                          }
                        >
                          <td className="whitespace-nowrap px-4 py-3">
                            <div className="flex items-center gap-2">
                              {teamMember.isSystem ? (
                                <div
                                  className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-0.5"
                                  title="Homecoming"
                                >
                                  <img
                                    src={teamMember.imageUrl}
                                    alt="Homecoming"
                                    className="h-full w-full object-contain"
                                  />
                                </div>
                              ) : (
                                <Avatar
                                  size="small"
                                  imageUrl={teamMember.imageUrl}
                                  name={teamMember.name}
                                />
                              )}
                              <div className="min-w-0">
                                <span className="block text-caption text-neutral-150">
                                  {teamMember.name}
                                </span>
                                {teamMember.subtitle && (
                                  <span className="block text-small-caption text-secondary-125">
                                    {teamMember.subtitle}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span
                              className={
                                teamMember.isSystem
                                  ? 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-25 px-2 py-0.5 text-small-label font-medium text-secondary-150'
                                  : 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-neutral-50 px-2 py-0.5 text-small-label font-medium text-neutral-150'
                              }
                            >
                              {getActionIcon(activity.activityType)}
                              {actionLabel}
                            </span>
                          </td>
                          <td className="max-w-[220px] px-4 py-3 text-small-caption text-neutral-125">
                            <div className="flex min-w-0 items-center gap-2">
                              {renderResourceThumbnail(metadata?.resource_kind)}
                              <span
                                className={`${clampedTextClassName} flex-1 text-caption text-neutral-150`}
                                title={resourceName}
                              >
                                {resourceName}
                              </span>
                            </div>
                          </td>
                          <td className="max-w-[280px] px-4 py-3 text-small-caption text-neutral-125">
                            <span
                              className={clampedTextClassName}
                              title={details}
                            >
                              {details}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-small-caption text-neutral-125">
                            <span
                              title={
                                activity.performedAt
                                  ? getFullDateTimeStamp(activity.performedAt)
                                  : ''
                              }
                            >
                              {activity.performedAt
                                ? getRelativeTimestamp(activity.performedAt)
                                : '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {allActivities.length > 0 && hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="flex items-center gap-1 text-caption font-medium text-secondary-100 hover:text-secondary-150 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Spinner className="h-4 w-4" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load more
                      <ChevronDownIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            )}
            {!loading && loadError && (
              <div className="py-12 text-center text-caption text-red-100">
                {loadError}
              </div>
            )}
            {!loading && !loadError && allActivities.length === 0 && (
              <div className="py-12 text-center text-caption text-neutral-125">
                No Library activity yet.
              </div>
            )}
          </>
        )}
      </div>
    </ModalDialog>
  );
};

export default LibraryActivityModal;
